export const SELECTOR_KINDS = Object.freeze([
    'entity',
    'state',
    'attribute',
    'designer-property',
    'number',
    'text',
    'boolean',
    'select',
    'service',
    'target',
    'device',
    'area',
    'multi_select',
    'color_rgb',
    'duration',
    'template',
    'object',
    'dynamic',
])

export function createSelectorModel(kind, options = {}) {
    const normalizedKind = SELECTOR_KINDS.includes(kind) ? kind : 'text'
    return {
        kind: normalizedKind,
        label: options.label ?? '',
        description: options.description ?? '',
        required: options.required === true,
        default: options.default,
        options: Array.isArray(options.options) ? options.options : [],
        filters: options.filters ?? {},
        entityId: options.entityId ?? '',
        recommendedDomains: options.recommendedDomains ?? [],
        includeAll: options.includeAll !== false,
        literalKind: options.literalKind ?? 'text',
        dynamicVariables: Array.isArray(options.dynamicVariables) ? options.dynamicVariables : [],
        suggestions: Array.isArray(options.suggestions) ? options.suggestions : [],
        min: options.min,
        max: options.max,
        step: options.step,
        preserveUnknown: options.preserveUnknown !== false,
        dynamic: options.dynamic === true,
        config: options.config ?? {},
    }
}

export function optionValue(option) {
    return option && typeof option === 'object' ? option.value : option
}

export function normalizeSelectorOptions(options = []) {
    return options.map(option => {
        const value = optionValue(option)
        return {
            value: String(value ?? ''),
            label: String(option?.label ?? value ?? ''),
            ...((option && typeof option === 'object') ? option : {}),
        }
    }).filter(option => option.value)
}

/** Read HA's one-key selector object without making the component know HA's schema shape. */
export function selectorDefinition(definition = {}) {
    const selector = definition?.selector
    if (!selector || typeof selector !== 'object' || Array.isArray(selector)) {
        return { kind: 'text', config: {} }
    }
    const [kind] = Object.keys(selector)
    return { kind: kind || 'text', config: kind ? selector[kind] ?? {} : {} }
}

/**
 * Translate a Home Assistant service-field selector into the Designer's
 * uniform selector model. HA remains the source of field metadata; this
 * function only maps it to the Vue editor contract.
 */
export function selectorForServiceField(definition = {}, {
    entityId = '',
    runtimeVariables = [],
    suggestions = [],
} = {}) {
    const { kind: sourceKind, config } = selectorDefinition(definition)
    const common = {
        label: definition.name,
        description: definition.description,
        required: definition.required === true,
        default: definition.default,
        entityId,
        dynamicVariables: runtimeVariables,
        options: config.options ?? [],
        min: config.min,
        max: config.max,
        step: config.step,
        suggestions,
        preserveUnknown: true,
    }

    if (sourceKind === 'entity') {
        return createSelectorModel('entity', {
            ...common,
            filters: { domain: config.domain ?? '' },
            config,
        })
    }
    if (sourceKind === 'target') return createSelectorModel('target', { ...common, config })
    if (sourceKind === 'device') return createSelectorModel('device', { ...common, config })
    if (sourceKind === 'area') return createSelectorModel('area', { ...common, config })
    if (sourceKind === 'select') {
        return createSelectorModel(config.multiple ? 'multi_select' : 'select', { ...common, config })
    }
    if (sourceKind === 'number') return createSelectorModel('dynamic', { ...common, literalKind: 'number', config })
    if (sourceKind === 'boolean') return createSelectorModel('boolean', { ...common, config })
    if (sourceKind === 'state') return createSelectorModel('state', { ...common, config })
    if (sourceKind === 'attribute') return createSelectorModel('attribute', { ...common, config })
    if (sourceKind === 'color_rgb') return createSelectorModel('dynamic', { ...common, literalKind: 'color_rgb', config })
    if (sourceKind === 'duration') return createSelectorModel('dynamic', { ...common, literalKind: 'duration', config })
    if (sourceKind === 'template') return createSelectorModel('dynamic', { ...common, literalKind: 'template', config })
    if (sourceKind === 'object') return createSelectorModel('dynamic', { ...common, literalKind: 'object', config })

    // Unknown HA selector kinds stay editable. Dynamic mode is useful here as
    // well because a saved Jinja expression must not be forced through a
    // literal editor just because the current frontend does not know its kind.
    return createSelectorModel('dynamic', { ...common, literalKind: 'text', config })
}

